"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import { FaXmark } from "react-icons/fa6";
import AdvertisePopup from "./AdvertisePopup";
import { injectAd, incrementNavigationCounter } from "@/lib/gpt-loader";
import { useWebsiteSetting, useRoutePageIds } from "./AdDataProvider";
import { useDevice } from '@/contexts/DeviceContext';

type AdSlotEntry = { desktopAdCode: string | null; mobileAdCode: string | null } | null;

interface SmartLayoutAdsProps {
  position: "sticky" | "lhs" | "rhs" | "top" | "anchor";
  className?: string;
  adCodeConfig?: Record<string, any> | null;
}

const AD_PLACEHOLDER_SIZES: Record<string, { minWidth: number; minHeight: number }> = {
  sticky: { minWidth: 320, minHeight: 50 },
  lhs: { minWidth: 160, minHeight: 250 },
  rhs: { minWidth: 300, minHeight: 250 },
  top: { minWidth: 728, minHeight: 90 },
  // Anchor format renders its own fixed bar; placeholder isn't visible.
  anchor: { minWidth: 0, minHeight: 0 },
};

type PageType = "home" | "section" | "article";

/**
 * Resolve page_id from pathname using route patterns.
 * Handles exact matches and dynamic patterns like /{slug}, /elections/{slug}
 */
function resolvePageId(pathname: string, routePageIds: Record<string, string>): string | null {
  // Exact match first
  if (routePageIds[pathname]) return routePageIds[pathname];

  // Pattern match: replace last segment with {slug}
  const segments = pathname.split('/').filter(Boolean);
  for (let i = segments.length; i >= 1; i--) {
    const pattern = '/' + segments.slice(0, i - 1).concat('{slug}').join('/');
    if (routePageIds[pattern]) return routePageIds[pattern];
  }

  // Try /{slug} for single-segment paths
  if (segments.length === 1 && routePageIds['/{slug}']) {
    return routePageIds['/{slug}'];
  }

  return null;
}

function detectPageType(pathname: string): PageType {
  if (pathname === "/" || pathname === "") return "home";
  const segments = pathname.split("/").filter(Boolean);
  if (segments.length >= 5) return "article";
  if (segments.length >= 1) return "section";
  return "home";
}

/**
 * Resolve the ad code for a given position from the ad-code config,
 * based on page type, device, pathname, and specificPageAds.
 */
function resolveAdCode(
  config: Record<string, any> | null,
  position: "sticky" | "lhs" | "rhs" | "top" | "anchor",
  pageType: PageType,
  isMobile: boolean,
  pathname: string,
  currentPageId: string | null
): string | null {
  if (!config) return null;

  const posMap: Record<string, string> = { top: 'Top', lhs: 'Lhs', rhs: 'Rhs', sticky: 'Sticky', anchor: 'Anchor' };
  const pos = posMap[position];

  // Check specificPageAds first
  if (config.hasSpecificPageAds && Array.isArray(config.specificPageAds)) {
    for (const entry of config.specificPageAds) {
      if (!entry.pages || !Array.isArray(entry.pages)) continue;
      const matches = entry.pages.some((p: any) => {
        if (!p) return false;
        if (currentPageId && p.id && p.id === currentPageId) return true;
        if (p.name) {
          const slug = p.name.toLowerCase().replace(/\s+/g, '-');
          if (pathname === `/${slug}` || pathname.startsWith(`/${slug}/`)) return true;
        }
        return false;
      });
      if (matches) {
        const desktopKey = `${position}AdDesktop`;
        const mobileKey = `${position}AdMobile`;
        const desktop = entry[desktopKey] || null;
        const mobile = entry[mobileKey] || null;
        if (desktop || mobile) {
          return isMobile ? (mobile || desktop) : desktop;
        }
      }
    }
  }

  // Default: use page-type-based codes
  const pageTypeMap: Record<string, string> = {
    home: 'homepage',
    section: 'sectionPage',
    article: 'articlePage',
  };
  const pt = pageTypeMap[pageType];

  const desktopCode = config[`${pt}${pos}AdDesktop`] || null;
  const mobileCode = config[`${pt}${pos}AdMobile`] || null;

  return isMobile ? (mobileCode || desktopCode) : desktopCode;
}

/**
 * Get the default page-type-based ad code (ignoring specificPageAds).
 * Used as a fallback when the page-specific ad returns empty.
 */
function resolveDefaultAdCode(
  config: Record<string, any> | null,
  position: "sticky" | "lhs" | "rhs" | "top" | "anchor",
  pageType: PageType,
  isMobile: boolean
): string | null {
  if (!config) return null;
  const posMap: Record<string, string> = { top: 'Top', lhs: 'Lhs', rhs: 'Rhs', sticky: 'Sticky', anchor: 'Anchor' };
  const pos = posMap[position];
  const pageTypeMap: Record<string, string> = {
    home: 'homepage',
    section: 'sectionPage',
    article: 'articlePage',
  };
  const pt = pageTypeMap[pageType];
  const desktopCode = config[`${pt}${pos}AdDesktop`] || null;
  const mobileCode = config[`${pt}${pos}AdMobile`] || null;
  return isMobile ? (mobileCode || desktopCode) : desktopCode;
}

export default function SmartLayoutAds({
  position,
  className = "",
  adCodeConfig: propConfig,
}: SmartLayoutAdsProps) {
  const adContentRef = useRef<HTMLDivElement>(null);
  const hasInjectedRef = useRef(false);
  const prevAdCodeRef = useRef<string | null>(null);
  const [isClosed, setIsClosed] = useState(false);
  const { isMobile, deviceDetected } = useDevice();
  const pathname = usePathname();
  const routePageIds = useRoutePageIds();

  const adCodeConfig = propConfig || null;

  const placeholderSize = AD_PLACEHOLDER_SIZES[position] || { minWidth: 300, minHeight: 250 };
  const pageType = detectPageType(pathname);

  const currentPageId = resolvePageId(pathname, routePageIds);

  const adCode = deviceDetected
    ? resolveAdCode(adCodeConfig, position, pageType, isMobile, pathname, currentPageId)
    : null;

  // Reset injection when pathname changes (client-side navigation)
  useEffect(() => {
    // Destroy any GPT slots in this container before clearing
    if (adContentRef.current && typeof window !== 'undefined') {
      const divs = adContentRef.current.querySelectorAll('[id^="div-gpt-ad-"]');
      if (divs.length > 0) {
        const divIds = Array.from(divs).map(d => d.id);
        const googletag = (window as any).googletag;
        if (googletag) {
          googletag.cmd = googletag.cmd || [];
          googletag.cmd.push(() => {
            try {
              const slotsToDestroy = googletag.pubads().getSlots().filter(
                (slot: any) => divIds.includes(slot.getSlotElementId())
              );
              if (slotsToDestroy.length > 0) {
                googletag.destroySlots(slotsToDestroy);
              }
            } catch { /* ignore */ }
          });
        }
      }
    }

    hasInjectedRef.current = false;
    prevAdCodeRef.current = null;
    if (adContentRef.current) {
      adContentRef.current.innerHTML = '';
    }
    incrementNavigationCounter(pathname);
  }, [pathname]);


  const hideStickyAdSetting = useWebsiteSetting('hide-sticky-ad-slot');
  const hideStickyAd = hideStickyAdSetting?.value === true || hideStickyAdSetting?.value === 'true';

  // Each position gets gated by its own per-page-type status flag, e.g.
  //   articlePageLhsAdStatus, articlePageTopAdStatus, articlePageAnchorAdStatus.
  // false hides the slot. Undefined defaults to true (backwards compat).
  const pageTypeKey = pageType === 'home' ? 'homepage' : pageType === 'section' ? 'sectionPage' : 'articlePage';
  const positionFieldMap: Record<string, string> = { top: 'Top', lhs: 'Lhs', rhs: 'Rhs', sticky: 'Sticky', anchor: 'Anchor' };
  const ownStatusKey = `${pageTypeKey}${positionFieldMap[position]}AdStatus`;
  const rawOwnStatus = adCodeConfig?.[ownStatusKey];
  const enabledForPage = rawOwnStatus !== false && rawOwnStatus !== 'false';

  // Anchor and sticky are mutually exclusive at the bottom of the viewport.
  // Sticky also needs to know if anchor is on for this page type to step aside.
  const anchorStatusKey = `${pageTypeKey}AnchorAdStatus`;
  const rawAnchorStatus = adCodeConfig?.[anchorStatusKey];
  const anchorEnabledForPage = rawAnchorStatus === true || rawAnchorStatus === 'true';

  // Inject ad code — waits for deviceDetected for all positions (TOP/LHS/RHS/Sticky)
  useEffect(() => {
    if (!deviceDetected) return;
    if (!adCode || !adContentRef.current) return;
    if (hasInjectedRef.current && prevAdCodeRef.current === adCode) return;
    if (!enabledForPage) return;
    if (position === "sticky" && (hideStickyAd || anchorEnabledForPage)) return;
    if (position === "top" && isMobile) return;

    if (adContentRef.current?.innerHTML) {
      adContentRef.current.innerHTML = '';
    }
    hasInjectedRef.current = true;
    prevAdCodeRef.current = adCode;
    injectAd(adContentRef.current!, adCode, `SmartLayoutAds-${position}`);

    // Fallback: if the specific page ad didn't render an iframe after 2s,
    // try the default page-type ad code
    const defaultCode = resolveDefaultAdCode(adCodeConfig, position, pageType, isMobile);
    if (defaultCode && defaultCode !== adCode) {
      const fallbackTimer = setTimeout(() => {
        if (!adContentRef.current) return;
        const hasIframe = adContentRef.current.querySelector('iframe');
        if (!hasIframe) {
          // Specific ad didn't render — fall back to default
          adContentRef.current.innerHTML = '';
          prevAdCodeRef.current = defaultCode;
          injectAd(adContentRef.current, defaultCode, `SmartLayoutAds-${position}-fallback`);
        }
      }, 2000);
      return () => clearTimeout(fallbackTimer);
    }
  }, [adCode, position, pathname, hideStickyAd, enabledForPage, anchorEnabledForPage, deviceDetected, isMobile, adCodeConfig, pageType]);

  // Universal status gate — admin can switch any position off via
  // ${pageType}${Position}AdStatus = false.
  if (!enabledForPage) return null;
  // Sticky: also yields to anchor when both could show on this page type.
  if (position === "sticky" && (hideStickyAd || isClosed || !deviceDetected || !adCode || anchorEnabledForPage)) return null;
  // Anchor: needs an ad code present (the per-page status was already checked above).
  if (position === "anchor" && (!deviceDetected || !adCode)) return null;
  // Other positions: always render container (even empty) to prevent CLS
  // The container is already sized by its parent div in layout.tsx

  const CloseButton = () => (
    <button
      onClick={() => setIsClosed(true)}
      className="absolute top-0 right-0 w-8 h-8 flex items-center justify-center text-grey rounded-full z-[9999] transition-colors cursor-pointer"
      aria-label="Close ad"
    >
      <FaXmark size={14} />
    </button>
  );


  const AdWrapper = ({ children, showAdvertise = true }: { children: React.ReactNode; showAdvertise?: boolean }) => (
    <div className="m-[5px]">
      <div className="bg-gray-100 p-[5px] relative">
        {children}
        {showAdvertise && <AdvertisePopup />}
      </div>
    </div>
  );

  if (position === "sticky") {
    return (
      <div className={`flex justify-center fixed bottom-0 left-0 right-0 z-50 ${className}`}>
        <div className="relative inline-block">
          <CloseButton />
          <AdWrapper showAdvertise={false}>
            <div ref={adContentRef} className="flex justify-center items-center" />
          </AdWrapper>
        </div>
      </div>
    );
  }

  // Anchor: render in normal document flow (matches the previous layout-code
  // behaviour). The served code uses defineOutOfPageSlot(adUnit, divId) and the
  // creative carries its own anchor styling/positioning. Wrapping it in a
  // position:fixed parent changes the containing block for the GPT iframe and
  // prevents some anchor creatives from rendering — so we don't.
  if (position === "anchor") {
    return (
      <div className={className} aria-hidden="true">
        <div ref={adContentRef} />
      </div>
    );
  }

  if (position === "top") {
    // Reserve 90px (desktop banner 728x90) so the slot never shifts content below
    // when gpt.js fills the slot or returns no-fill. Layout.tsx wraps this in
    // `min-h-[90px]` too, but setting it here keeps the component self-sufficient.
    return (
      <div
        className={`w-full flex justify-center ${className}`}
        style={{ minHeight: 90 }}
      >
        <AdWrapper>
          <div ref={adContentRef} style={{ minWidth: 728, minHeight: 90 }} />
        </AdWrapper>
      </div>
    );
  }

  // LHS/RHS: no AdWrapper padding/margin — 160px container is tight for the 160x600 ad
  if (position === "lhs" || position === "rhs") {
    return (
      <div className={`relative ${className}`} style={{ maxWidth: '100%' }}>
        <div ref={adContentRef} />
      </div>
    );
  }

  return (
    <div className={`relative overflow-hidden ${className}`} style={{ maxWidth: '100%' }}>
      <AdWrapper>
        <div ref={adContentRef} className="overflow-hidden" style={{ maxWidth: '100%' }} />
      </AdWrapper>
    </div>
  );
}
